/**
 * SelectInput component with support for both single and multiple selection
 *
 * Usage examples:
 *
 * Single selection:
 * <SelectInput
 *   options={[{ label: "Option 1", value: "1" }, { label: "Option 2", value: "2" }]}
 *   placeholder="Choose an option"
 * />
 *
 * Multiple selection:
 * <SelectInput
 *   multiple
 *   options={[{ label: "Option 1", value: "1" }, { label: "Option 2", value: "2" }]}
 *   placeholder="Choose options"
 *   defaultValue={["1"]} // Array of values for multiple selection
 * />
 *
 * With async options:
 * <SelectInput
 *   multiple
 *   requestOptions={async (params) => {
 *     // Fetch options from API
 *     return [{ label: "Option 1", value: "1" }];
 *   }}
 *   searchable
 *   searchKey="search"
 * />
 */
import { trans } from "@mongez/localization";
import type { GenericObject } from "@mongez/reinforcements";
import { CheckmarkIcon } from "react-hot-toast";
import { useSelect } from "~/design-system/hooks";
import type { Option } from "~/design-system/types";
import { cn } from "~/shared/lib";
import { Button } from "../button";
import { ChevronDown, Remove } from "../icons";
import type { BaseInputProps } from "./BaseInput";
import { ErrorInput } from "./ErrorInput";
import { LabelInput } from "./LabelInput";

type SelectWithOptions = {
  options: Option[];
  requestOptions?: (params?: GenericObject) => Promise<Option[]>;
};

type SelectWithRequest = {
  options?: Option[];
  requestOptions: (params?: GenericObject) => Promise<Option[]>;
};

export type SelectInputProps = BaseInputProps &
  (SelectWithRequest | SelectWithOptions) & {
    searchable?: boolean;
    searchKey?: string;
    multiple?: boolean;
    classes?: {
      wrapper?: string;
      label?: string;
      input?: string;
      options?: string;
      option?: string;
      search?: string;
      selectedOptions?: string;
      selectedOption?: string;
      removeButton?: string;
      placeholder?: string;
    };
  };

export function SelectInput({
  classes,
  placeholder,
  multiple = false,
  label,
  ...props
}: SelectInputProps) {
  const {
    id,
    error,
    openOptionsMenu,
    selectedOptions,
    selectRef,
    searchRef,
    searchable,
    multiple: isMultiple,
    setOpenOptionsMenu,
    handleSelectOption,
    removeSelectedOption,
    isOptionSelected,
    loadingOptions,
    filteredOptions,
    setSearchValue,
  } = useSelect({ ...props, multiple });

  const renderSelectedOptions = () => {
    if (isMultiple && selectedOptions.length > 0) {
      return (
        <div className={cn("flex flex-wrap gap-2", classes?.selectedOptions)}>
          {selectedOptions.map((option) => (
            <div
              key={option.value}
              className={cn(
                "flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs sm:text-sm lg:text-base",
                classes?.selectedOption,
              )}
            >
              <span>{option.label}</span>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  removeSelectedOption(option);
                }}
                className={cn(
                  "ml-1 rounded-full p-1 text-red-500 hover:bg-gray-100",
                  classes?.removeButton,
                )}
              >
                <Remove className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <span className={cn(classes?.placeholder)}>
        {selectedOptions.length > 0
          ? selectedOptions[0].label
          : placeholder || trans("select")}
      </span>
    );
  };

  return (
    <div
      ref={selectRef}
      className={cn("relative flex flex-col gap-2", classes?.wrapper)}
    >
      <LabelInput
        htmlFor={id}
        required={props.required}
        className={cn(classes?.label, error && "text-red-500")}
      >
        {label}
      </LabelInput>

      <Button
        type="button"
        onClick={() => setOpenOptionsMenu(!openOptionsMenu)}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-full border border-black px-6 py-4 text-xs sm:text-sm lg:text-base",
          error && "border-red-500",
          classes?.input,
        )}
      >
        <div className="flex flex-1 flex-col items-start">
          {renderSelectedOptions()}
        </div>
        <span>
          <ChevronDown />
        </span>
      </Button>

      {openOptionsMenu && (
        <div
          className={cn(
            "absolute top-[calc(100%+0.5rem)] left-0 z-50 flex h-fit w-full flex-col gap-4 overflow-hidden rounded-xl border bg-white shadow-lg",
          )}
        >
          {searchable && (
            <input
              type="search"
              ref={searchRef}
              placeholder={trans("search")}
              onChange={(e) => setSearchValue(e.target.value)}
              className={cn(
                "mx-8 rounded-xl border border-gray-500 px-2 py-2 text-base placeholder:text-gray-500 focus:outline-none",
                classes?.search,
              )}
            />
          )}
          <div className="flex max-h-[180px] flex-col gap-4 overflow-y-auto p-1">
            {loadingOptions ? (
              <div className="flex h-full w-full items-center justify-center">
                Loading...
              </div>
            ) : filteredOptions.length > 0 ? (
              <ul className={cn("flex h-fit flex-col gap-1", classes?.options)}>
                {filteredOptions.map((option) => (
                  <li key={option.value}>
                    <Button
                      onClick={() => handleSelectOption(option)}
                      className={cn(
                        "hover:bg-primary-light w-full rounded-xl px-3 py-1.5 text-start",
                        isOptionSelected(option) && "bg-primary-light",
                        classes?.option,
                      )}
                    >
                      {isMultiple && (
                        <span className="mr-2">
                          {isOptionSelected(option) && (
                            <CheckmarkIcon className="h-4 w-4" />
                          )}
                        </span>
                      )}
                      {option.label}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex h-full w-full items-center justify-center py-6">
                No options found
              </div>
            )}
          </div>
        </div>
      )}

      <ErrorInput>{error}</ErrorInput>
    </div>
  );
}
